import { Injectable, inject } from "@angular/core";
import { Router } from "@angular/router";

import { GlobalStateStore } from "@services/global-state/global-state.service";
import { RobService } from "@services/rob/rob.service";
import { AccountType } from "../../types/AccountType";
import * as constants from "@services/content/content";
import { QueryBuilderService } from "@services/query/query-builder.service";
import { WebLogger } from "@weps/shared-http";

export interface SaveResult {
  success: boolean;
  error?: string;
}

@Injectable({
  providedIn: "root",
})
export class GuidedFlowModalService {
  private readonly globalStore = inject(GlobalStateStore);
  private readonly robService = inject(RobService);
  private readonly router = inject(Router);
  private readonly queryBuilder = inject(QueryBuilderService);

  async showSaveModal(
    accountType: AccountType,
    isEdit: boolean,
    ownerType: string,
  ): Promise<SaveResult> {
    try {
      // Show loading modal
      this.showLoadingModal(isEdit);

      // Trigger modal display
      this.triggerModalDisplay();

      //TODO: in ZOK, verify if householdMemberId is a uuid
      //TODO: in ROB, we should verify if the ownerId exists when creating/updating accounts so that we don't have an issue with atomicity.
      // Build individual mutation queries using QueryBuilderService
      const ownerQueries = this.queryBuilder.buildOwnerQueries(ownerType);
      const accountQuery = this.queryBuilder.buildAccountQuery(accountType); // Always exists

      //TODO: Band-aid, currently if you go thru the vg trust flow, it creates two of the same queries.
      if (accountType == AccountType.TRUST) {
        ownerQueries.pop();
      }

      // Combine multiple GraphQL mutations using aliases into a single request
      const combinedMutation = this.queryBuilder.combineQueriesWithAliases(
        ownerQueries,
        accountQuery,
      );

      // Execute the combined mutation through ROB service
      this.robService.executeCombinedMutation(combinedMutation);

      // Wait for mutation result
      const result = await this.waitForMutationResult();

      if (result.success) {
        // Show success state briefly
        this.globalStore.updateModalSyncState("success");
        this.globalStore.updateModalSyncMessage(constants.GUIDED_FLOW_PLACEHOLDER_SUCCESS_CONTENT);

        // Refetch estate profile to get the updated account data including payOnDeath
        this.robService.getRobData();

        // Close modal and navigate
        this.closeModal();
        await this.router.navigate(["/personal-wealth-inventory"]);

        return { success: true };
      } else {
        this.handleModalErrorState(isEdit);
        return { success: false, error: result.error };
      }
    } catch (error) {
      this.handleModalErrorState(isEdit);
      return { success: false, error: error instanceof Error ? error.message : "Unknown error" };
    }
  }

  private showLoadingModal(isEdit: boolean): void {
    this.globalStore.updateModalSyncState("loading");

    if (isEdit) {
      this.globalStore.updateModalSyncMessage(constants.GUIDED_FLOW_EDIT_ACCOUNT_LOADING_CONTENT);
    } else {
      this.globalStore.updateModalSyncMessage(constants.GUIDED_FLOW_ADD_ACCOUNT_LOADING_CONTENT);
    }
  }

  private triggerModalDisplay(): void {
    const modalButton: HTMLElement | null = document.querySelector("#continue-confirm-button");
    modalButton?.setAttribute("data-bs-toggle", "modal");
    modalButton?.setAttribute("data-bs-target", "#sync-status-modal");
    modalButton?.click();
  }

  private closeModal(): void {
    const modalButton: HTMLElement | null = document.querySelector("#continue-confirm-button");
    modalButton?.click();
  }

  private handleModalErrorState(isEdit: boolean): void {
    this.globalStore.updateModalSyncState("error");

    if (isEdit) {
      this.globalStore.updateModalSyncMessage(constants.GUIDED_FLOW_EDIT_ACCOUNT_ERROR_CONTENT);
    } else {
      this.globalStore.updateModalSyncMessage(constants.GUIDED_FLOW_ADD_ACCOUNT_ERROR_CONTENT);
    }
  }

  private waitForMutationResult(): Promise<SaveResult> {
    return new Promise((resolve) => {
      const mutationSubscription = this.robService.getRobMutationObservable().subscribe({
        next: (response) => {
          if (response.errors.length === 0) {
            // Refresh data after successful mutation
            this.robService.getRobData();

            const dataSubscription = this.robService.getRobDataLoadedObservable().subscribe({
              next: () => {
                dataSubscription.unsubscribe();
                mutationSubscription.unsubscribe();
                resolve({ success: true });
              },
              error: (error) => {
                dataSubscription.unsubscribe();
                mutationSubscription.unsubscribe();
                WebLogger.log({
                  message: "GuidedFlowModal data reload after mutation error",
                  level: "error",
                  payload: { error },
                });
                resolve({ success: false, error: "Failed to reload data after save" });
              },
            });
          } else {
            mutationSubscription.unsubscribe();
            WebLogger.log({
              message: "GuidedFlowModal mutation response error",
              level: "error",
              payload: { errors: response.errors },
            });
            resolve({ success: false, error: "Mutation failed" });
          }
        },
        error: (error) => {
          mutationSubscription.unsubscribe();
          WebLogger.log({
            message: "GuidedFlowModal mutation error",
            level: "error",
            payload: { error },
          });
          resolve({ success: false, error: "Network error during save" });
        },
      });
    });
  }
}
